import React, { Fragment } from 'react';
import PropTypes from 'prop-types';

import {
  Paper,
  Text,
  Space,
  Conditional,
  Toggle,
  Popup,
} from '@cashfree-intl/coherent';
import { useSearchParams } from 'react-router-dom';
import { getIcon } from 'payments-icons-library';

import CustomIcon from 'components/CustomIcons';
import { TEST, CURRENCY_SYMBOL } from 'utils/constants';
import Analytics from 'utils/analytics';

import { getDisabledPaperClassName, getPaymentMethodIconName } from './utils';
import {
  DISABLED,
  REJECTED,
  AVAILABLE,
  STATUS_COLOR_MAPPER,
  STATUS_MAPPER,
  ACTIVATED,
  PAYTM_CODE,
  PAYPAL_CODE,
  NOT_AVAILABLE,
  NEGATIVE_MCC_CODES_FOR_UPI,
  DIGITAL_GOODS_GAMES,
  WALLET_TOP_UP,
  DISABLED_REASON,
  DISABLED_REASON_MAPPER,
  NOT_CONFIGURABLE_MESSAGE,
  REAL_ESTATE_AGENTS_AND_MANAGERS_RENTALS,
  LAZYPAY_CODE,
  PAY_LATER,
  CASH_ON_DELIVERY_CODE,
} from '../constants';
import Paypal from './Paypal';
import Paytm from './Paytm';
import UPI from './UPI';
import { StyledPaymentMethodIcon } from './styled';
import Cod from './Cod';

function PaymentMethodsListItem({
  showListing,
  expandItem,
  setExpandItem,
  onToggleClick,
  paytmData,
  paypalData,
  mcc,
  disableInternationalCards,
  disablePaymentGroup,
  data = [],
  onCodSubmit,
  cashOnDeliveryData,
}) {
  const [searchParams] = useSearchParams();
  const env = searchParams.get('env');

  const isTest = env === TEST;

  let [firstList, ...rest] = data;
  const {
    display = '',
    uniqueIdentifier = '',
    status,
    attributes: firstListAttributes,
  } = firstList;

  if (
    firstListAttributes.length === 0 &&
    rest.some(obj => obj.attributes.length > 0)
  ) {
    const [first, ...others] = rest.filter(obj => obj.attributes.length > 0);
    firstList = first;
    rest = others;
  }

  const isNegativeMCC = NEGATIVE_MCC_CODES_FOR_UPI.includes(mcc);

  const onListItemClick = e => {
    e.stopPropagation();
    setExpandItem(expandItem ? '' : display);
    Analytics.trackEventWithNewNomenclature({
      eventName: 'Form_PaymentMethodsListItem_Icon_Expand',
    });
  };

  const onToggle = (mode, attributes = []) => () => {
    if (
      mode.parentCode === PAYPAL_CODE &&
      mode.status?.trim().toUpperCase() === DISABLED
    ) {
      paypalData.onToggleClick(false);
    } else {
      // filter the attributes here
      // - only AVAILABLE status
      // - only isConfigurable true
      const combinedModes = attributes.filter(
        obj =>
          obj.isConfigurable &&
          obj.status === AVAILABLE &&
          obj.parentCode !== mode.parentCode,
      );

      onToggleClick({ mode, display, combinedModes });
    }
  };

  const renderPaymentMethod = ({ mode, index, isFirstSection, attributes }) => {
    const notAvailableStatus =
      mode.status?.trim().toUpperCase() === NOT_AVAILABLE;
    const isNotAvailableOrDisabledStatus = [NOT_AVAILABLE, DISABLED].includes(
      mode.status?.trim().toUpperCase(),
    );

    let disableToggle = mode.status !== AVAILABLE || disableInternationalCards;

    if (
      mode.parentCode === PAYPAL_CODE &&
      mode.status?.trim().toUpperCase() === DISABLED
    ) {
      disableToggle = false;
    }

    const { icon_name: iconName, icon_url: iconUrl } = getIcon(
      getPaymentMethodIconName(mode.parentCode),
      'svg',
    );

    let displayPaymentMethod =
      ![PAYTM_CODE, CASH_ON_DELIVERY_CODE].includes(mode.parentCode) ||
      isNotAvailableOrDisabledStatus;

    if (mode.parentCode === PAYPAL_CODE && !notAvailableStatus)
      displayPaymentMethod = false;

    return (
      <Fragment key={mode.parentCode}>
        <Conditional
          if={mode.parentCode === PAYTM_CODE && !isNotAvailableOrDisabledStatus}
        >
          <Paytm
            isTest={isTest}
            accountStatus={mode.status === ACTIVATED ? 'ACTIVE' : 'PENDING'}
            tdr={mode.tdr}
            {...paytmData}
          />
        </Conditional>
        <Conditional
          if={mode.parentCode === PAYPAL_CODE && !notAvailableStatus}
        >
          <Paypal isTest={isTest} {...paypalData} />
        </Conditional>
        <Conditional
          if={
            mode.parentCode === CASH_ON_DELIVERY_CODE &&
            !isNotAvailableOrDisabledStatus
          }
        >
          <Cod
            isTest={isTest}
            mode={mode}
            attributes={attributes}
            onToggle={onToggle}
            onCodSubmit={onCodSubmit}
            cashOnDeliveryData={cashOnDeliveryData}
            disableToggle={disableToggle}
          />
        </Conditional>
        <Conditional if={displayPaymentMethod}>
          <div
            className={`payment-mode ${notAvailableStatus ? 'blur' : ''} ${
              index === 0 ? '' : 'pt-3'
            } ${isFirstSection && expandItem && showListing ? 'pt-3' : ''} ${
              !showListing ? 'pt-0' : ''
            }`}
          >
            <div className="display-text-section">
              <Conditional if={iconName !== 'default'}>
                <StyledPaymentMethodIcon className={iconName} src={iconUrl} />
              </Conditional>
              <Conditional if={iconName === 'default'}>
                <CustomIcon componentName="NETBANKING" />
              </Conditional>
              <Text variant="b14" color="bodyLight">
                {mode.display}
                <Conditional if={mode.parentCode === LAZYPAY_CODE}>
                  <sup className="asterisk first">*</sup>
                </Conditional>
              </Text>
            </div>
            <Conditional if={!notAvailableStatus}>
              <div />
            </Conditional>
            <Conditional if={notAvailableStatus}>
              <Text variant="b14" color="bodyLight">
                This payment mode is not applicable to you due to your merchant
                category.
              </Text>
            </Conditional>
            <Space alignItems="center" justifyContent="end" gap={3}>
              {/* for DISABLED, REJECTED, IN_REVIEW -> opacity and disable the toggle */}
              {/* AVAILABLE and ACTIVATED ->  */}
              <Conditional
                if={
                  STATUS_COLOR_MAPPER[mode.status] &&
                  (!isTest ||
                    mode.status?.trim().toUpperCase() === ACTIVATED) &&
                  !(
                    mode.parentCode === PAYPAL_CODE &&
                    mode.status?.trim().toUpperCase() === DISABLED
                  )
                }
              >
                <Space alignItems="center">
                  <Text color={STATUS_COLOR_MAPPER[mode.status]}>
                    {STATUS_MAPPER[mode.status]}
                  </Text>
                  <Conditional if={[REJECTED, DISABLED].includes(mode.status)}>
                    <Popup
                      content={DISABLED_REASON.replace(
                        '$reason$',
                        `${DISABLED_REASON_MAPPER[mode.reason?.toUpperCase()] ||
                          ''}`,
                      )}
                      trigger={
                        <span className="info pointer reason ml-1">
                          <CustomIcon componentName="INFO" />
                        </span>
                      }
                      position="top right"
                    />
                  </Conditional>
                </Space>
              </Conditional>
              <Conditional if={!isTest}>
                <Conditional if={mode.isConfigurable}>
                  <div data-testid="toggle">
                    <Toggle
                      data-event-name="Table_Row_Toggle"
                      data-ignore-inner-text="true"
                      data-event-properties={JSON.stringify({
                        row_id: mode,
                      })}
                      disabled={disableToggle}
                      active={mode.status === ACTIVATED}
                      onToggle={onToggle(mode, attributes)}
                    />
                  </div>
                </Conditional>
                <Conditional if={!mode.isConfigurable}>
                  <Popup
                    content={NOT_CONFIGURABLE_MESSAGE}
                    trigger={
                      <div>
                        <Toggle
                          disabled
                          active={mode.status === ACTIVATED}
                          onToggle={onToggle(mode, attributes)}
                        />
                      </div>
                    }
                    position="top right"
                  />
                </Conditional>
              </Conditional>
            </Space>
          </div>
        </Conditional>
      </Fragment>
    );
  };

  return (
    <div
      className={getDisabledPaperClassName(
        disableInternationalCards,
        status?.trim().toUpperCase() === DISABLED,
      )}
    >
      <Paper
        depth={expandItem}
        className={`mt-2 paper-divider px-0 ${data.length > 1 ? '' : 'mb-2'}`}
      >
        <Conditional if={uniqueIdentifier === 'UPI'}>
          {firstList.attributes.length > 0 ? (
            <UPI
              data={firstList}
              isTest={isTest}
              mcc={mcc}
              onToggle={onToggle}
            />
          ) : (
            <Text color="bodyLight" className="ml-3">
              No results found
            </Text>
          )}
        </Conditional>
        <Conditional if={uniqueIdentifier !== 'UPI'}>
          <Conditional if={showListing}>
            <Space
              alignItems="center"
              justifyContent="space-between"
              className={`item-chevron pointer ${expandItem ? 'mb-3' : ''}`}
              onClick={onListItemClick}
              data-event-name="Form_PaymentMethodsListItem_Icon_Expand"
              data-ignore-inner-text="true"
            >
              <Text variant="h16">{display}</Text>
              <CustomIcon
                componentName="CHEVRONDOWN"
                direction={expandItem ? 'up' : 'down'}
              />
            </Space>
          </Conditional>
          <Conditional if={expandItem}>
            {firstList.attributes.length > 0 ? (
              firstList.attributes?.map((obj, index) =>
                renderPaymentMethod({
                  mode: obj,
                  index,
                  isFirstSection: index === 0,
                  attributes: firstList.attributes,
                }),
              )
            ) : (
              <Text color="bodyLight" className="ml-3">
                No results found
              </Text>
            )}
          </Conditional>
        </Conditional>
      </Paper>
      <Conditional if={expandItem && rest.length > 0}>
        {rest.map(
          ({ attributes, uniqueIdentifier: id }, i) =>
            attributes.length > 0 && (
              <Paper
                depth
                key={id}
                className={i < rest.length - 1 ? 'paper-divider px-0' : ''}
              >
                {attributes?.map((obj, index) =>
                  renderPaymentMethod({ mode: obj, index, attributes }),
                )}
              </Paper>
            ),
        )}
      </Conditional>
      <Conditional if={isNegativeMCC && uniqueIdentifier === 'UPI'}>
        <Conditional if={mcc === DIGITAL_GOODS_GAMES}>
          <Text className="mt-3" color="bodyLight">
            As per the circular UPI OC 141 released by the National Payments
            Corporation of India (NPCI), merchants with merchant category code
            5816 (Digital Goods: Games) are not allowed to initiate UPI collect
            requests, except for transactions upto Rs 2000 per day for iOS
            device users.
          </Text>
        </Conditional>
        <Conditional if={mcc === REAL_ESTATE_AGENTS_AND_MANAGERS_RENTALS}>
          <Text className="mt-3" color="bodyLight">
            As per the circular UPI OC 141 released by the National Payments
            Corporation of India (NPCI), merchants with merchant category code
            6513 (Real Estate Agents and Managers: Rentals) are not allowed to
            initiate UPI collect requests.
          </Text>
        </Conditional>
        <Conditional if={mcc === WALLET_TOP_UP}>
          <Text className="mt-3" color="bodyLight">
            As per the circular UPI OC 141 released by the National Payments
            Corporation of India (NPCI), merchants with merchant category code
            6540 (Wallet top-up) are not allowed to initiate UPI collect
            requests.
          </Text>
        </Conditional>
      </Conditional>
      <Conditional
        if={
          status?.trim().toUpperCase() === DISABLED &&
          expandItem &&
          !disablePaymentGroup
        }
      >
        <Space
          alignItems="center"
          justifyContent="center"
          className="disabled-mode-paper-wrapper"
        >
          <Paper className="p-2 disabled-mode-paper">
            This payment method is disabled for your account. Contact your
            account manager or write to us at{' '}
            <span className="w-700">care@cashfree.com</span> to enable it.
            Please note that payment modes will resume their previous states
            after enablement.
          </Paper>
        </Space>
      </Conditional>
      <Conditional if={uniqueIdentifier === PAY_LATER}>
        <Text className="mt-3" color="bodyLight">
          <sup className="asterisk last">*</sup>This payment method is only
          available for transactions upto {CURRENCY_SYMBOL.INR} 2000.
        </Text>
      </Conditional>
    </div>
  );
}

PaymentMethodsListItem.propTypes = {
  showListing: PropTypes.bool.isRequired,
  data: PropTypes.array.isRequired,
  setExpandItem: PropTypes.func.isRequired,
  expandItem: PropTypes.bool.isRequired,
  paytmData: PropTypes.object,
  paypalData: PropTypes.object,
  activatePaypalCurrency: PropTypes.func,
  deactivatePaypalCurrency: PropTypes.func,
  updateDefaultPaypalCurrency: PropTypes.func,
  onToggleClick: PropTypes.func.isRequired,
  onPaypalToggleClick: PropTypes.func,
  mcc: PropTypes.number.isRequired,
  disableInternationalCards: PropTypes.bool.isRequired,
  disablePaymentGroup: PropTypes.bool.isRequired,
  onCodSubmit: PropTypes.func,
  cashOnDeliveryData: PropTypes.object,
};

export default PaymentMethodsListItem;
